import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { FormListItem, Icon, Tag, Text } from '@growtherapy/sprout-ui';

import { MeasureBundleObject, MeasureBundleScores } from '@grow-therapy-team/seedling-components';

import { GroupedFormData } from '../../components/forms/types';
import { filterNonNullable } from '../../utils';
import { groupedFormsByTitleForSelectedClientAtom } from '../client-information/state';
import { useOpenClientInformationCallback } from '../client-information/useClientInformationDrawerCallbacks';
import { useOpenClientFormsCallback } from '../client-information/useOpenClientFormsCallback';
import {
  ProviderDrawerState,
  drawerStateAtom,
  isInSessionWithSelectedClientAtom,
  selectedClientInfoAtom,
  selectedClientMeasuresData,
} from '../state';
import {
  FORM_LIST_CLIENT_FORM_INCOMPLETE_ARIA_LABEL,
  FORM_LIST_CLIENT_MEASURE_INCOMPLETE_ARIA_LABEL,
  FORM_LIST_CSSRS_FORM_DESCRIPTION,
  FORM_LIST_CSSRS_FORM_TITLE,
  FORM_LIST_INCOMPLETE_TAG,
  FORM_LIST_INTAKE_FORM_TITLE,
  FORM_LIST_LOCALE_DATE_OPTIONS,
  FORM_LIST_MEASURES_COMPLETED_PREFIX,
  FORM_LIST_MEASURES_LAST_COMPLETED_PREFIX,
  FORM_LIST_MEASURES_PARTIALLY_COMPLETED_PREFIX,
  FORM_LIST_MEASURES_TITLE,
  FORM_LIST_SAFETY_PLAN_FORM_TITLE,
  FORM_LIST_SEEKING_HELP_FOR_PREFIX,
  FORM_LIST_SUBMITTED_PREFIX,
  FORM_LIST_THERAPY_REASONS_QUESTION_NAME,
} from './constants';
import { getBundleCompletionState } from './utils';

const ITEM_CLASSNAME_REBRAND =
  'rebrand:bg-surface-inverseEmphasis rebrand:hover:bg-surface-inverseEmphasis [&_p]:rebrand:text-text-inverse [&_span]:rebrand:text-text-inverse [&_svg]:rebrand:text-text-inverse';

function Action() {
  return <Icon aria-hidden icon={faChevronRight} className='mr-2' />;
}

export function MeasuresFormListItem({
  bundles,
  shouldAllowCompletion,
}: {
  bundles: MeasureBundleObject[];
  shouldAllowCompletion: boolean;
}) {
  const { state, lastCompletion } = getBundleCompletionState(bundles);
  const formattedLastCompletion = lastCompletion
    ? lastCompletion.toLocaleDateString(undefined, FORM_LIST_LOCALE_DATE_OPTIONS)
    : null;

  const setDrawerState = useSetAtom(drawerStateAtom);
  const props = useMemo(() => {
    const measureBundleItems = filterNonNullable(bundles[0]?.measureBundleItems ?? []);

    switch (state) {
      case 'completed':
        return {
          title: FORM_LIST_MEASURES_TITLE,
          description: (
            <>
              <Text variant='sm' as='span' className='text-neutral-700'>
                {FORM_LIST_MEASURES_COMPLETED_PREFIX}
                {formattedLastCompletion}
              </Text>
              <MeasureBundleScores measureBundleItems={measureBundleItems} />
            </>
          ),
          onClick: () => setDrawerState(ProviderDrawerState.MEASURE_RESPONSES),
        };
      case 'partially_completed':
        return {
          title: (
            <div className='flex gap-x-2 items-center'>
              <Text>{FORM_LIST_MEASURES_TITLE}</Text>
              <Tag aria-label={FORM_LIST_CLIENT_MEASURE_INCOMPLETE_ARIA_LABEL} use='yellow'>
                {FORM_LIST_INCOMPLETE_TAG}
              </Tag>
            </div>
          ),
          description: (
            <>
              <Text variant='sm' as='span' className='text-neutral-700'>
                {FORM_LIST_MEASURES_PARTIALLY_COMPLETED_PREFIX}
                {formattedLastCompletion}
              </Text>
              <MeasureBundleScores measureBundleItems={measureBundleItems} />
            </>
          ),
        };
      default:
        return {
          title: (
            <div className='flex gap-x-2 items-center'>
              <Text>{FORM_LIST_MEASURES_TITLE}</Text>
              <Tag aria-label={FORM_LIST_CLIENT_MEASURE_INCOMPLETE_ARIA_LABEL} use='yellow'>
                {FORM_LIST_INCOMPLETE_TAG}
              </Tag>
            </div>
          ),
          description: formattedLastCompletion && (
            <Text variant='sm' as='span' className='text-neutral-700'>
              {FORM_LIST_MEASURES_LAST_COMPLETED_PREFIX}
              {formattedLastCompletion}
            </Text>
          ),
        };
    }
  }, [state, bundles, formattedLastCompletion, setDrawerState]);

  if (state !== 'completed' && !shouldAllowCompletion) return;

  return (
    <FormListItem
      onClick={() => setDrawerState(ProviderDrawerState.PENDING_MEASURES)}
      action={<Action />}
      {...props}
      className={ITEM_CLASSNAME_REBRAND}
    />
  );
}

export function MeasuresFormListItemWrapper() {
  const measureBundles = useAtomValue(selectedClientMeasuresData);
  const shouldAllowCompletion = useAtomValue(isInSessionWithSelectedClientAtom);

  return <MeasuresFormListItem bundles={measureBundles} shouldAllowCompletion={shouldAllowCompletion} />;
}

export function SafetyPlanFormListItem({
  form,
  onOpenCompletedForm,
  onOpenIncompleteForm,
  shouldAllowCompletion,
}: {
  form: GroupedFormData;
  onOpenCompletedForm: (formTitle: string) => void;
  onOpenIncompleteForm: () => void;
  shouldAllowCompletion: boolean;
}) {
  const complete = !!form?.formResponseId;
  const props = useMemo(() => {
    if (complete) {
      const submitted = new Date(form.createdAt).toLocaleDateString(undefined, FORM_LIST_LOCALE_DATE_OPTIONS);
      return {
        title: form.formTitle,
        description: `${FORM_LIST_SUBMITTED_PREFIX}${submitted}`,
        onClick: () => onOpenCompletedForm(form.formTitle),
      };
    }
    return {
      title: (
        <div className='flex gap-x-2 items-center'>
          <Text>{form.formTitle}</Text>
          <Tag aria-label={FORM_LIST_CLIENT_FORM_INCOMPLETE_ARIA_LABEL} use='yellow'>
            {FORM_LIST_INCOMPLETE_TAG}
          </Tag>
        </div>
      ),
      description: null,
      onClick: () => onOpenIncompleteForm(),
    };
  }, [complete, form, onOpenCompletedForm, onOpenIncompleteForm]);

  if (!complete && !shouldAllowCompletion) return;

  return <FormListItem action={<Action />} {...props} className={ITEM_CLASSNAME_REBRAND} />;
}

export function SafetyPlanFormListItemWrapper() {
  const setDrawerState = useSetAtom(drawerStateAtom);
  const onOpenCompletedForm = useOpenClientFormsCallback();
  const shouldAllowCompletion = useAtomValue(isInSessionWithSelectedClientAtom);
  const onOpenIncompleteForm = () => setDrawerState(ProviderDrawerState.PENDING_FORMS);
  const forms = useAtomValue(groupedFormsByTitleForSelectedClientAtom);
  const form = forms?.[FORM_LIST_SAFETY_PLAN_FORM_TITLE]?.[0];

  return (
    form && (
      <SafetyPlanFormListItem
        form={form}
        onOpenCompletedForm={onOpenCompletedForm}
        onOpenIncompleteForm={onOpenIncompleteForm}
        shouldAllowCompletion={shouldAllowCompletion}
      />
    )
  );
}

/** Intake forms can only be viewed. They cannot be filled out within Telehealth. */
export function IntakeFormListItem({
  form,
  onOpenForm,
}: {
  form: GroupedFormData;
  onOpenForm: (formTitle: string) => void;
}) {
  if (!form?.formResponseId) return null;

  const submitted = new Date(form.createdAt).toLocaleDateString(undefined, FORM_LIST_LOCALE_DATE_OPTIONS);
  const seekingHelpFor = form.formAnswers[0].elements.find(
    (el) => el.name == FORM_LIST_THERAPY_REASONS_QUESTION_NAME,
  )?.value;
  const description = (
    <>
      <Text variant='sm'>
        {FORM_LIST_SUBMITTED_PREFIX}
        {submitted}
      </Text>
      {seekingHelpFor && (
        <Text variant='sm' className='italic'>
          {FORM_LIST_SEEKING_HELP_FOR_PREFIX}
          {seekingHelpFor}
        </Text>
      )}
    </>
  );
  return (
    <FormListItem
      title={form.formTitle}
      description={description}
      onClick={() => onOpenForm(form.formTitle)}
      action={<Action />}
      className={ITEM_CLASSNAME_REBRAND}
    />
  );
}

export function IntakeFormListItemWrapper() {
  const onOpenForm = useOpenClientFormsCallback();
  const forms = useAtomValue(groupedFormsByTitleForSelectedClientAtom);
  const form = forms?.[FORM_LIST_INTAKE_FORM_TITLE]?.[0];

  return form && <IntakeFormListItem form={form} onOpenForm={onOpenForm} />;
}

export function CSSRSFormListItem({ onOpenForm }: { onOpenForm: () => void }) {
  return (
    <FormListItem
      title={FORM_LIST_CSSRS_FORM_TITLE}
      description={FORM_LIST_CSSRS_FORM_DESCRIPTION}
      onClick={onOpenForm}
      action={<Action />}
      className={ITEM_CLASSNAME_REBRAND}
    />
  );
}

export function CSSRSFormListItemWrapper() {
  const openClientInformation = useOpenClientInformationCallback();
  const selectedClient = useAtomValue(selectedClientInfoAtom);
  const patientShortId = selectedClient?.patientShortId;
  const visitorUuid = selectedClient?.visitorUuid;

  const onOpenForm = useCallback(() => {
    if (!patientShortId) return;
    void openClientInformation({ visitorUuid, patientShortId }, ProviderDrawerState.CSSRS_FORM);
  }, [openClientInformation, patientShortId, visitorUuid]);

  return <CSSRSFormListItem onOpenForm={onOpenForm} />;
}
